/**
 * Prüfregeln für IBAN und BIC nach Norm (Story 2.14): IBAN nach ISO 13616 mit dem SWIFT-IBAN-Register (Länge und
 * Aufbau der Kontonummer je Land), BIC nach ISO 9362 mit Ländercode nach ISO 3166-1. Das sind Normen, keine
 * Fachwerte (deshalb Tabellen im Code, keine Einstellung). Reines TypeScript ohne Server-Abhängigkeiten.
 */

/** Ländercodes nach ISO 3166-1 alpha-2 (aktuell zugeteilt) plus XK (Kosovo, im IBAN-Register und bei BICs genutzt). */
export const ISO_COUNTRY_CODES: ReadonlySet<string> = new Set('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(' '));

/**
 * IBAN-Register: Gesamtlänge der IBAN je Land und, wo einfach, der Aufbau der Kontonummer (BBAN, ohne Land und
 * Prüfziffer). Alle SEPA-Länder und weitere häufige; ohne Muster gilt „Buchstaben und Ziffern in der Länge“.
 */
const IBAN_REGISTRY: Readonly<Record<string, { length: number; bban?: RegExp }>> = {
  AD: { length: 24, bban: /^\d{8}[A-Z0-9]{12}$/ },
  AE: { length: 23, bban: /^\d{19}$/ },
  AL: { length: 28, bban: /^\d{8}[A-Z0-9]{16}$/ },
  AT: { length: 20, bban: /^\d{16}$/ },
  AZ: { length: 28, bban: /^[A-Z]{4}[A-Z0-9]{20}$/ },
  BA: { length: 20, bban: /^\d{16}$/ },
  BE: { length: 16, bban: /^\d{12}$/ },
  BG: { length: 22, bban: /^[A-Z]{4}\d{6}[A-Z0-9]{8}$/ },
  BH: { length: 22, bban: /^[A-Z]{4}[A-Z0-9]{14}$/ },
  BR: { length: 29, bban: /^\d{23}[A-Z][A-Z0-9]$/ },
  BY: { length: 28, bban: /^[A-Z0-9]{4}\d{4}[A-Z0-9]{16}$/ },
  CH: { length: 21, bban: /^\d{5}[A-Z0-9]{12}$/ },
  CR: { length: 22, bban: /^\d{18}$/ },
  CY: { length: 28, bban: /^\d{8}[A-Z0-9]{16}$/ },
  CZ: { length: 24, bban: /^\d{20}$/ },
  DE: { length: 22, bban: /^\d{18}$/ },
  DK: { length: 18, bban: /^\d{14}$/ },
  DO: { length: 28, bban: /^[A-Z0-9]{4}\d{20}$/ },
  EE: { length: 20, bban: /^\d{16}$/ },
  EG: { length: 29, bban: /^\d{25}$/ },
  ES: { length: 24, bban: /^\d{20}$/ },
  FI: { length: 18, bban: /^\d{14}$/ },
  FO: { length: 18, bban: /^\d{14}$/ },
  FR: { length: 27, bban: /^\d{10}[A-Z0-9]{11}\d{2}$/ },
  GB: { length: 22, bban: /^[A-Z]{4}\d{14}$/ },
  GE: { length: 22, bban: /^[A-Z]{2}\d{16}$/ },
  GI: { length: 23, bban: /^[A-Z]{4}[A-Z0-9]{15}$/ },
  GL: { length: 18, bban: /^\d{14}$/ },
  GR: { length: 27, bban: /^\d{7}[A-Z0-9]{16}$/ },
  GT: { length: 28, bban: /^[A-Z0-9]{24}$/ },
  HR: { length: 21, bban: /^\d{17}$/ },
  HU: { length: 28, bban: /^\d{24}$/ },
  IE: { length: 22, bban: /^[A-Z]{4}\d{14}$/ },
  IL: { length: 23, bban: /^\d{19}$/ },
  IQ: { length: 23, bban: /^[A-Z]{4}\d{15}$/ },
  IS: { length: 26, bban: /^\d{22}$/ },
  IT: { length: 27, bban: /^[A-Z]\d{10}[A-Z0-9]{12}$/ },
  JO: { length: 30, bban: /^[A-Z]{4}\d{4}[A-Z0-9]{18}$/ },
  KW: { length: 30, bban: /^[A-Z]{4}[A-Z0-9]{22}$/ },
  KZ: { length: 20, bban: /^\d{3}[A-Z0-9]{13}$/ },
  LB: { length: 28, bban: /^\d{4}[A-Z0-9]{20}$/ },
  LC: { length: 32, bban: /^[A-Z]{4}[A-Z0-9]{24}$/ },
  LI: { length: 21, bban: /^\d{5}[A-Z0-9]{12}$/ },
  LT: { length: 20, bban: /^\d{16}$/ },
  LU: { length: 20, bban: /^\d{3}[A-Z0-9]{13}$/ },
  LV: { length: 21, bban: /^[A-Z]{4}[A-Z0-9]{13}$/ },
  MC: { length: 27, bban: /^\d{10}[A-Z0-9]{11}\d{2}$/ },
  MD: { length: 24, bban: /^[A-Z0-9]{20}$/ },
  ME: { length: 22, bban: /^\d{18}$/ },
  MK: { length: 19, bban: /^\d{3}[A-Z0-9]{10}\d{2}$/ },
  MR: { length: 27, bban: /^\d{23}$/ },
  MT: { length: 31, bban: /^[A-Z]{4}\d{5}[A-Z0-9]{18}$/ },
  MU: { length: 30, bban: /^[A-Z]{4}\d{19}[A-Z]{3}$/ },
  NL: { length: 18, bban: /^[A-Z]{4}\d{10}$/ },
  NO: { length: 15, bban: /^\d{11}$/ },
  PK: { length: 24, bban: /^[A-Z]{4}[A-Z0-9]{16}$/ },
  PL: { length: 28, bban: /^\d{24}$/ },
  PS: { length: 29, bban: /^[A-Z]{4}[A-Z0-9]{21}$/ },
  PT: { length: 25, bban: /^\d{21}$/ },
  QA: { length: 29, bban: /^[A-Z]{4}[A-Z0-9]{21}$/ },
  RO: { length: 24, bban: /^[A-Z]{4}[A-Z0-9]{16}$/ },
  RS: { length: 22, bban: /^\d{18}$/ },
  SA: { length: 24, bban: /^\d{2}[A-Z0-9]{18}$/ },
  SC: { length: 31, bban: /^[A-Z]{4}\d{20}[A-Z]{3}$/ },
  SE: { length: 24, bban: /^\d{20}$/ },
  SI: { length: 19, bban: /^\d{15}$/ },
  SK: { length: 24, bban: /^\d{20}$/ },
  SM: { length: 27, bban: /^[A-Z]\d{10}[A-Z0-9]{12}$/ },
  ST: { length: 25, bban: /^\d{21}$/ },
  SV: { length: 28, bban: /^[A-Z]{4}\d{20}$/ },
  TL: { length: 23, bban: /^\d{19}$/ },
  TN: { length: 24, bban: /^\d{20}$/ },
  TR: { length: 26, bban: /^\d{5}[A-Z0-9]{17}$/ },
  UA: { length: 29, bban: /^\d{6}[A-Z0-9]{19}$/ },
  VA: { length: 22, bban: /^\d{18}$/ },
  VG: { length: 24, bban: /^[A-Z]{4}\d{16}$/ },
  XK: { length: 20, bban: /^\d{16}$/ },
};

/** IBAN ohne Leerzeichen in Großbuchstaben. */
export function normalizeIban(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

/**
 * IBAN prüfen: Land im IBAN-Register, Länge und Aufbau der Kontonummer des Landes, Prüfziffer (mod 97 = 1, ohne
 * Gleitkomma).
 */
export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false;
  const entry = IBAN_REGISTRY[iban.slice(0, 2)];
  if (!entry || iban.length !== entry.length) return false;
  if (entry.bban && !entry.bban.test(iban.slice(4))) return false;
  const moved = `${iban.slice(4)}${iban.slice(0, 4)}`;
  let rest = 0;
  for (const ch of moved) {
    const digits = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55);
    for (const d of digits) rest = (rest * 10 + Number(d)) % 97;
  }
  return rest === 1;
}

/**
 * BIC prüfen (ISO 9362): 8 oder 11 Zeichen; 1–4 Bank (Buchstaben), 5–6 Land (ISO 3166-1), 7–8 Ort, 9–11 Filiale
 * (Buchstaben/Ziffern). Ob es die Bank gibt, prüft das ERP nicht (keine Bankenliste).
 */
export function isValidBic(value: string): boolean {
  const bic = value.replace(/\s+/g, '').toUpperCase();
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic) && ISO_COUNTRY_CODES.has(bic.slice(4, 6));
}
